import { generateApiStoreKey, getResourceKey } from './utils';
import { IStore } from './store';
import { useSelector } from 'react-redux';
import { ApiNameType, LocationType, ResourceKey, StoreNameType } from './models/subscriptions';
import { ApiRequestStatus } from './models/apiRequestResult';
import { useCallback, useEffect, useState } from 'react';
import { getConfig, setConfig } from './configuration/configuration';
import { FrontendLocations } from './constants/locations';
import { useLocalStorageValue } from '@react-hookz/web';
import {
  CountryAlpha3Code,
  HOME_PAGE_TYPE_LOCAL_STORAGE_KEY,
  SELECTED_MARKET_LOCAL_STORAGE_KEY,
  StorageKey,
  ZOOM_LEVEL_LOCAL_STORAGE_KEY
} from '@cmctechnology/webinvest-constants';
import { HOME_PAGE_TYPES, HomePageTypes } from './constants/homepageTypes';
import { useFrontendContext } from './FrontendContext';

declare var cmcsb: {
  webApiUrl: string;
};

export const useFrontendLocationSelector = <T extends string | string[]>(
  location: LocationType,
  storeName: StoreNameType,
  apiName: ApiNameType,
  isCollection?: boolean
): T | string => {
  const storeKey = generateApiStoreKey(storeName, apiName);
  return <T>useSelector((store: IStore) => getResourceKey(store.frontend?.locations?.[location]?.[storeKey], isCollection));
};

export const useFrontendStatus = (storeName: StoreNameType, apiName: ApiNameType, resourceKey?: ResourceKey) => {
  const storeKey = generateApiStoreKey(storeName, apiName, resourceKey);

  const status = useSelector((store: IStore) => store.frontend.apiResults[storeKey]?.status);
  const error = useSelector((store: IStore) => store.frontend.apiResults[storeKey]?.errorCode);
  const fetching = status === ApiRequestStatus.InProgress;

  return { fetching, error, status };
};

export const useFrontendSubscriptionStatus = (storeName: StoreNameType, apiName: ApiNameType, resourceKey?: ResourceKey) => {
  const frontendStatus = useFrontendStatus(storeName, apiName, resourceKey);

  const storeKey = generateApiStoreKey(storeName, apiName);
  const polling = useSelector((store: IStore) => store.frontend.subscriptions[storeKey]?.[resourceKey ?? '']?.length > 0);

  return {
    ...frontendStatus,
    polling
  };
};

export const useWfeWebApiConfig = () => {
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    if ((window as any).cmcsb?.webApiUrl) {
      const config = { ...getConfig() };
      config.stockbrokingApiUrl = cmcsb.webApiUrl;
      setConfig(config);
    }
    setCompleted(true);
  }, []);

  return completed;
};

export const createLocalStorageShortKey = (key: string, location?: string) => {
  return location ? `${location}.${key}` : key;
};

export const useLocalStorage: <T>(key: StorageKey | string, initialValue: T, location?: FrontendLocations) => [T, (newValue: T) => void] = <T>(
  key: StorageKey | string,
  initialValue: T,
  location?: FrontendLocations
) => {
  const shortKey = createLocalStorageShortKey(key, location);
  const { value, set } = useLocalStorageValue(shortKey, {
    defaultValue: initialValue
  });

  return [value as T, set];
};

type ExpiresT<T> = { t: T; e: number };
export const useLocalStorageExpires: <T>(
  key: StorageKey | string,
  initialValue: T,
  expiryMs: number,
  location?: FrontendLocations
) => [T, (newValue: T) => void] = <T>(key: StorageKey | string, initialValue: T, expiryMs: number, location?: FrontendLocations) => {
  const shortKey = createLocalStorageShortKey(key, location);
  const initialValueWithExpiry: ExpiresT<T> = { t: initialValue, e: Date.now().valueOf() };
  let { value: lsValue, set: setLsValue } = useLocalStorageValue<ExpiresT<T>>(shortKey, {
    defaultValue: initialValueWithExpiry
  });

  // no deps array so expiry is checked on every render
  useEffect(() => {
    if (lsValue && Date.now().valueOf() - expiryMs >= lsValue.e) setLsValue(initialValueWithExpiry);
  });

  const setWithExpiry = useCallback((newValue: T) => setLsValue({ t: newValue, e: Date.now().valueOf() }), []);

  return [lsValue?.t as T, setWithExpiry];
};

const prefixFeatureFlag = (feature: string) => (feature.startsWith('ff-') ? feature : `ff-${feature}`);

/**
 * @description Basic feature flag that can be set manually through local storage, or via query param.
 * @param feature the name of the feature flag. If not prefixed with 'ff-', it will be prefixed by hook.
 */
export const useLocalFeatureFlag = (feature: string): boolean => {
  const name = prefixFeatureFlag(feature);
  const [flag, setFlag] = useLocalStorage(prefixFeatureFlag(name), '0');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flagValue = params.get(name);
    if (flagValue) {
      setFlag(flagValue === 'true' || flagValue === '1' ? '1' : '0');
    }
  }, []);
  return flag === '1';
};

export const useDefaultHomePage = () => {
  const [defaultLandingPageType, setDefaultLandingPage] = useLocalStorage<HomePageTypes>(
    HOME_PAGE_TYPE_LOCAL_STORAGE_KEY,
    HOME_PAGE_TYPES.DASHBOARD,
    FrontendLocations.Navigation
  );

  const [selectedMarket, setSelectedMarket] = useLocalStorage<CountryAlpha3Code>(
    SELECTED_MARKET_LOCAL_STORAGE_KEY,
    CountryAlpha3Code.USA,
    FrontendLocations.Navigation
  );

  return [defaultLandingPageType, setDefaultLandingPage, selectedMarket, setSelectedMarket] as const;
};

export const useZoom = () => {
  const [zoom, setZoom] = useLocalStorage<number>(ZOOM_LEVEL_LOCAL_STORAGE_KEY, 1);
  const zoomStyle = {
    zoom: zoom
  };
  return { zoom, setZoom, zoomStyle };
};

export const useDisableZoom = () => {
  const [zoom] = useLocalStorage<number>(ZOOM_LEVEL_LOCAL_STORAGE_KEY, 1);
  const { containerRef } = useFrontendContext();

  useEffect(() => {
    if (containerRef && containerRef.current) {
      containerRef.current?.style.setProperty('zoom', `1`);
    }

    return () => {
      if (!containerRef?.current) return;
      containerRef!.current!.style.setProperty('zoom', `${zoom}`);
    };
  });
};
